import React, {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";

const TOOLTIP_GAP = 8;

/**
 * Tooltip component
 *
 * Renders an accessible tooltip shown on hover or keyboard focus.
 * Supports directional placement relative to the trigger.
 *
 * @param {object} props
 * @param {string} props.message
 *   Text content displayed inside the tooltip container.
 *
 * @param {"top"|"bottom"|"left"|"right"} [props.direction="top"]
 *   Side where the tooltip appears relative to the trigger element.
 *
 * @param {React.ReactNode} props.children
 *   The trigger element; aria-describedby is injected for accessibility.
 *
 * @param {string} [props.className]
 *   Additional classes applied to the trigger wrapper container.
 *
 * @param {boolean} [props.disabled=false]
 *   If true, disables the tooltip and renders only children.
 *
 * @param {boolean} [props.open]
 * Controls the tooltip visibility when provided.
 */

export default function Tooltip({
    message,
    direction = "top",
    children,
    className = "",
    disabled = false,
    open: controlledOpen,
}) {
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen ?? internalOpen;
    const [mounted, setMounted] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({
        top: -9999,
        left: -9999,
    });
    const wrapperRef = useRef(null);
    const tooltipRef = useRef(null);
    const id = useId();

    const show = () => controlledOpen === undefined && setInternalOpen(true);
    const hide = () => controlledOpen === undefined && setInternalOpen(false);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setMounted(true);
        return () => setMounted(false);
    }, []);

    const updatePosition = useCallback(() => {
        if (!wrapperRef.current || !tooltipRef.current) return;

        const triggerRect = wrapperRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        let top = triggerRect.top - tooltipRect.height - TOOLTIP_GAP;
        let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

        if (direction === "bottom") {
            top = triggerRect.bottom + TOOLTIP_GAP;
        } else if (direction === "left") {
            top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
            left = triggerRect.left - tooltipRect.width - TOOLTIP_GAP;
        } else if (direction === "right") {
            top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
            left = triggerRect.right + TOOLTIP_GAP;
        }

        setTooltipPosition({
            top: Math.round(top),
            left: Math.round(left),
        });
    }, [direction]);

    useLayoutEffect(() => {
        if (!open || disabled) return;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        updatePosition();
    }, [open, updatePosition, message, disabled]);

    useEffect(() => {
        if (!open || disabled) return;
        const handlePosition = () => updatePosition();

        window.addEventListener("scroll", handlePosition, true);
        window.addEventListener("resize", handlePosition);

        return () => {
            window.removeEventListener("scroll", handlePosition, true);
            window.removeEventListener("resize", handlePosition);
        };
    }, [open, updatePosition, disabled]);

    if (!message || disabled) return children;

    function buildMessage() {
        return (
            <p className="font-ui font-body text-caption text-main-typography-system-inverted px-2 whitespace-pre-line">
                {message}
            </p>
        );
    }

    function buildTooltip() {
        if (!mounted) return null;

        return (
            createPortal(
                <div
                    id={id}
                    role="tooltip"
                    ref={tooltipRef}
                    className={`fixed z-[9999] px-3 py-2 rounded-sm max-w-xs
          bg-main-background-system-inverted
          ${open ? "opacity-100 scale-100" : "opacity-0 scale-95"}
          transition-[opacity,transform] duration-150 ease-out pointer-events-none`}
                    aria-hidden={open ? "false" : "true"}
                    style={{
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                    }}
                >
                    {buildMessage()}
                </div>,
                document.body,
            )
        );
    }

    return (
        <div
            ref={wrapperRef}
            className={`relative inline-block max-w-fit ${className}`}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
        >
            {React.cloneElement(children, { "aria-describedby": id })}
            {buildTooltip()}
        </div>
    );
}
